import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ErrorBoundary } from '@datadog/browser-rum-react';
// import { BrowserRouter } from '@rocketlane/rocket-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { QueryClientProvider as QueryClientProviderV5 } from '@tanstack/react-query-v5';
import moment from 'moment';
// Register the locales used across the app. Both bundlers run AntdDayjsWebpackPlugin
// (replaceMoment), which aliases `moment` → `dayjs`, so these `moment/locale/*`
// imports resolve to `dayjs/locale/*` and the bundle never pulls in real moment.
import 'moment/locale/de';
import 'moment/locale/es';
import 'moment/locale/fi';
import 'moment/locale/fr';
import 'moment/locale/it';
import 'moment/locale/ja';
import 'moment/locale/nl';
import 'moment/locale/pt';
import 'moment/locale/sv';
import { useEffect, useLayoutEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { UserAPI } from 'api/user';
import { AntdLocaleProvider } from 'components/atoms/antd-locale';
import { LocaleIntlProvider } from 'components/i18n/LocaleIntlProvider';
import { AppUpgradeConfirmation } from 'components/molecules/app-update-confirmation';
import { AppCrashIntercom } from 'components/organisms/app-crash/intercom';
import { GlobalStyle } from 'components/styles/global';
import { theme } from 'components/styles/theme';
import { APP_CRASH_KEY, POTENTIAL_APP_CRASH_KEY } from 'constant';
import { LANGUAGE_SELECT } from 'constant/constant';
import { AntdRlUISwitcherProvider } from 'contexts/antd-rlui-switcher';
import { CustomerPortalPreviewProvider } from 'contexts/providers';
import { getCurrentUserProfileState } from 'data/current-user-profile';
import { getBrowserLocale } from 'hooks/use-language-select';
import { OTAUpdatesComponent } from 'ota-updates';
import { OzoneFonts } from 'ozone/atoms';
import { PerfObserver } from 'performanceObserver';
import { queryClient } from 'queries';
import { queryClientV5 } from 'queries/client-v5';
import { logger } from 'services';
import { SetupOkta } from 'setupokta';
import { localeData } from 'translations/en-moment';
import { captureChunkErrorTelemetry, isChunkLoadError, markChunkErrorReloadAttempt, reloadAfterChunkError, reportChunkErrorReloadOutcome, shouldLogTerminalChunkError, stripCacheBusterParam, wasRecentlyReloadedForChunkError } from 'utils/chunk-error';
import { getDevEnv } from 'utils/is-localhost';
import { lazyWithRetry } from 'utils/lazyWithRetry';
import { db } from 'workers';
import { store } from './data';
const LazyReactQueryDevtools = lazyWithRetry(()=>// eslint-disable-next-line import/no-extraneous-dependencies
    import('@tanstack/react-query-devtools').then(({ ReactQueryDevtools })=>({
            default: ReactQueryDevtools
        })));
export const App = ()=>{
    const [locale, setLocale] = useState((typeof window !== 'undefined' ? localStorage.getItem(LANGUAGE_SELECT) : null) || getBrowserLocale() || 'en');
    const { isLocal, isCypress } = getDevEnv();
    const isTesting = process.env.PLAYWRIGHT_TESTS;
    // const navigation = useNavigation();
    useEffect(()=>{
        var _userProfile_data_user_emailLanguagePreference_toLowerCase, _userProfile_data_user_emailLanguagePreference, _userProfile_data_user, _userProfile_data;
        const unsubscribe = store.subscribe(()=>{
            var _userProfile_data_user_emailLanguagePreference_toLowerCase, _userProfile_data_user_emailLanguagePreference, _userProfile_data_user, _userProfile_data;
            const state = store.getState();
            const userProfile = getCurrentUserProfileState(state);
            const userLocale = userProfile === null || userProfile === void 0 ? void 0 : (_userProfile_data = userProfile.data) === null || _userProfile_data === void 0 ? void 0 : (_userProfile_data_user = _userProfile_data.user) === null || _userProfile_data_user === void 0 ? void 0 : (_userProfile_data_user_emailLanguagePreference = _userProfile_data_user.emailLanguagePreference) === null || _userProfile_data_user_emailLanguagePreference === void 0 ? void 0 : (_userProfile_data_user_emailLanguagePreference_toLowerCase = _userProfile_data_user_emailLanguagePreference.toLowerCase) === null || _userProfile_data_user_emailLanguagePreference_toLowerCase === void 0 ? void 0 : _userProfile_data_user_emailLanguagePreference_toLowerCase.call(_userProfile_data_user_emailLanguagePreference);
            if (userLocale) {
                setLocale((prevLocale)=>{
                    if (prevLocale !== userLocale) {
                        localStorage.setItem(LANGUAGE_SELECT, userLocale);
                        return userLocale;
                    }
                    return prevLocale;
                });
            }
        });
        // Also check initial state
        const initialState = store.getState();
        const userProfile = getCurrentUserProfileState(initialState);
        const userLocale = userProfile === null || userProfile === void 0 ? void 0 : (_userProfile_data = userProfile.data) === null || _userProfile_data === void 0 ? void 0 : (_userProfile_data_user = _userProfile_data.user) === null || _userProfile_data_user === void 0 ? void 0 : (_userProfile_data_user_emailLanguagePreference = _userProfile_data_user.emailLanguagePreference) === null || _userProfile_data_user_emailLanguagePreference === void 0 ? void 0 : (_userProfile_data_user_emailLanguagePreference_toLowerCase = _userProfile_data_user_emailLanguagePreference.toLowerCase) === null || _userProfile_data_user_emailLanguagePreference_toLowerCase === void 0 ? void 0 : _userProfile_data_user_emailLanguagePreference_toLowerCase.call(_userProfile_data_user_emailLanguagePreference);
        if (userLocale) {
            setLocale((prevLocale)=>{
                if (prevLocale !== userLocale) {
                    localStorage.setItem(LANGUAGE_SELECT, userLocale);
                    return userLocale;
                }
                return prevLocale;
            });
        }
        return unsubscribe;
    }, []);
    // Fallback: fetch profile on mount if user data is not available yet
    useEffect(()=>{
        var _userProfile_data;
        const state = store.getState();
        const userProfile = getCurrentUserProfileState(state);
        if (!(userProfile === null || userProfile === void 0 ? void 0 : (_userProfile_data = userProfile.data) === null || _userProfile_data === void 0 ? void 0 : _userProfile_data.user)) {
            UserAPI.getProfile().then((d)=>{
                var _d_user_emailLanguagePreference_toLowerCase, _d_user_emailLanguagePreference, _d_user;
                const userLocale = d === null || d === void 0 ? void 0 : (_d_user = d.user) === null || _d_user === void 0 ? void 0 : (_d_user_emailLanguagePreference = _d_user.emailLanguagePreference) === null || _d_user_emailLanguagePreference === void 0 ? void 0 : (_d_user_emailLanguagePreference_toLowerCase = _d_user_emailLanguagePreference.toLowerCase) === null || _d_user_emailLanguagePreference_toLowerCase === void 0 ? void 0 : _d_user_emailLanguagePreference_toLowerCase.call(_d_user_emailLanguagePreference);
                if (userLocale) {
                    setLocale(userLocale);
                    localStorage.setItem(LANGUAGE_SELECT, userLocale);
                }
            }).catch(()=>{
            // User is not authenticated yet (e.g. on /activate or /login pages)
            });
        }
    }, []);
    useLayoutEffect(()=>{
        var _locale_toLowerCase, _normalized_split, _normalized_split1;
        const normalized = locale === null || locale === void 0 ? void 0 : (_locale_toLowerCase = locale.toLowerCase) === null || _locale_toLowerCase === void 0 ? void 0 : _locale_toLowerCase.call(locale);
        const baseLang = normalized === null || normalized === void 0 ? void 0 : (_normalized_split1 = normalized.split) === null || _normalized_split1 === void 0 ? void 0 : (_normalized_split = _normalized_split1.call(normalized, '-')) === null || _normalized_split === void 0 ? void 0 : _normalized_split[0];
        if (baseLang === 'en') {
            moment.updateLocale('en', localeData);
        }
        moment.locale(baseLang);
    }, [
        locale
    ]);
    useEffect(()=>{
        stripCacheBusterParam();
        // If the previous page navigated here via reloadAfterChunkError, the
        // current boot is the first observable evidence the recovery worked
        // (the previous LogRocket session is destroyed by location.replace,
        // so we couldn't see the recovery from there). Track via the new
        // session so replays show post-reload outcomes.
        reportChunkErrorReloadOutcome();
    }, []);
    const AppUpgradeConfirmationComponent = ()=>/*#__PURE__*/ _jsx(QueryClientProvider, {
            client: queryClient,
            children: /*#__PURE__*/ _jsx(LocaleIntlProvider, {
                defaultLocale: "en",
                children: /*#__PURE__*/ _jsx(AppUpgradeConfirmation, {})
            })
        });
    const fallback = ({ error, // eslint-disable-next-line @typescript-eslint/no-unused-vars
    resetError })=>{
        var _error_message, _error_message1;
        /**
     * Handling errors from indexDb here
     * VersionError occurs on an existing window when the indexDb version is downgraded.
     * This causes the existing tab to break its connection, and the app crashes.
     * Trying to mitigate this by reloading
     * DatabaseClosedError occurs on an existing window when a new window upgrades indexDB version
     */ if (error === null || error === void 0 ? void 0 : (_error_message = error.message) === null || _error_message === void 0 ? void 0 : _error_message.includes('VersionError')) {
            logger.error(`${POTENTIAL_APP_CRASH_KEY}: ${error === null || error === void 0 ? void 0 : error.message}`, {
                error
            });
            db.delete();
            window.location.reload();
            return /*#__PURE__*/ _jsx(_Fragment, {});
        }
        // need to check if DatabaseClosedError occurs other than when upgrading
        if (error === null || error === void 0 ? void 0 : (_error_message1 = error.message) === null || _error_message1 === void 0 ? void 0 : _error_message1.includes('DatabaseClosedError')) {
            logger.error(`${POTENTIAL_APP_CRASH_KEY}: ${error === null || error === void 0 ? void 0 : error.message}`, {
                error
            });
            return /*#__PURE__*/ _jsx(_Fragment, {});
        }
        if (isChunkLoadError(error)) {
            var _telemetry_chunkUrl;
            const justReloaded = wasRecentlyReloadedForChunkError();
            if (!justReloaded) {
                // Auto-recovery path: about to reload. Suppress the Datadog/RUM
                // error log here — the `chunk-error-reload-outcome` LogRocket track
                // event captures the recovery in the next session, and the terminal
                // "already reloaded" branch below still logs the actionable
                // failures. (PR #17601 follow-up.)
                markChunkErrorReloadAttempt();
                reloadAfterChunkError(undefined, {
                    name: error === null || error === void 0 ? void 0 : error.name,
                    message: error === null || error === void 0 ? void 0 : error.message
                });
                return /*#__PURE__*/ _jsx(_Fragment, {});
            }
            // Terminal path: auto-reload happened, chunk still failing. Log with
            // the diagnostic telemetry for filterable analysis — but only once per
            // (session, chunkUrl): this fallback re-renders in the error state and
            // would otherwise re-log the same failure ~8x per session.
            const telemetry = captureChunkErrorTelemetry(error);
            if (shouldLogTerminalChunkError(String((_telemetry_chunkUrl = telemetry.chunkUrl) !== null && _telemetry_chunkUrl !== void 0 ? _telemetry_chunkUrl : ''), 'boundary')) {
                logger.error(`${APP_CRASH_KEY}: ChunkLoadError - already reloaded`, {
                    error,
                    ...telemetry
                });
            }
        }
        if (error) {
            // Chunk errors are already logged once (deduped) in the branch above.
            // Re-logging here is doubly harmful: logger.error mutates error.message
            // (see services/logger.ts), so each re-render re-prefixes it
            // (__app_crash: __app_crash: ...) into a new, ungroupable message and
            // spams the dashboard. Only log non-chunk errors here.
            if (!isChunkLoadError(error)) {
                logger.error(`${APP_CRASH_KEY}: ${error.message}`, {
                    error
                });
            }
            return /*#__PURE__*/ _jsx(LocaleIntlProvider, {
                defaultLocale: "en",
                children: /*#__PURE__*/ _jsx(QueryClientProvider, {
                    client: queryClient,
                    children: /*#__PURE__*/ _jsx(Provider, {
                        store: store,
                        children: /*#__PURE__*/ _jsx(AppCrashIntercom, {})
                    })
                })
            });
        }
        return /*#__PURE__*/ _jsx(_Fragment, {});
    };
    return /*#__PURE__*/ _jsx(ErrorBoundary, {
        fallback: fallback,
        children: /*#__PURE__*/ _jsxs(PerfObserver, {
            children: [
                /*#__PURE__*/ _jsx(HelmetProvider, {
                    children: /*#__PURE__*/ _jsx(CustomerPortalPreviewProvider, {
                        children: /*#__PURE__*/ _jsxs(QueryClientProvider, {
                            client: queryClient,
                            children: [
                                /*#__PURE__*/ _jsx(QueryClientProviderV5, {
                                    client: queryClientV5,
                                    children: /*#__PURE__*/ _jsx(Provider, {
                                        store: store,
                                        children: /*#__PURE__*/ _jsx(AntdRlUISwitcherProvider, {
                                            children: /*#__PURE__*/ _jsxs(ThemeProvider, {
                                                theme: theme,
                                                children: [
                                                    /*#__PURE__*/ _jsx(LocaleIntlProvider, {
                                                        defaultLocale: "en",
                                                        children: /*#__PURE__*/ _jsxs(AntdLocaleProvider, {
                                                            children: [
                                                                /*#__PURE__*/ _jsx(SetupOkta, {}),
                                                                /*#__PURE__*/ _jsx(OzoneFonts, {}),
                                                                /*#__PURE__*/ _jsx(OTAUpdatesComponent, {})
                                                            ]
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx(GlobalStyle, {})
                                                ]
                                            })
                                        })
                                    })
                                }),
                                isLocal && !isCypress && !isTesting ? /*#__PURE__*/ _jsx(LazyReactQueryDevtools, {
                                    position: "bottom-right"
                                }) : null
                            ]
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(AppUpgradeConfirmationComponent, {})
            ]
        })
    });
};
